/** Only this presentation contract can affect the embedded document. */
const colorTokens = new Set(['background', 'foreground', 'card', 'card-foreground', 'popover', 'popover-foreground', 'primary', 'primary-foreground', 'secondary', 'secondary-foreground', 'muted', 'muted-foreground', 'accent', 'accent-foreground', 'destructive', 'border', 'input', 'ring', 'violet', 'success', 'button', 'button-foreground', 'danger-fg', 'warning-fg', 'info-fg']);
const sizeTokens = new Set(['radius', 'radius-sm', 'radius-md', 'radius-xl', 'body-size', 'body-lh', 'small-size', 'small-lh', 'h1-size', 'h1-lh', 'h2-size', 'h2-lh', 'h3-size', 'h3-lh']);
function validTheme(value: unknown): value is { scheme: 'light' | 'dark'; font: 'inter'; tokens: Record<string, string> } {
  if (!value || typeof value !== 'object') return false;
  const theme = value as { scheme?: unknown; font?: unknown; tokens?: unknown };
  if (!['light', 'dark'].includes(String(theme.scheme)) || theme.font !== 'inter' || !theme.tokens || typeof theme.tokens !== 'object' || Array.isArray(theme.tokens)) return false;
  const entries = Object.entries(theme.tokens);
  if (entries.length === 0 || entries.length > colorTokens.size + sizeTokens.size) return false;
  return entries.every(([key, value]) => {
    if (typeof value !== 'string' || value.length > 128) return false;
    if (colorTokens.has(key)) {
      // No var(), URL, declaration delimiters, or custom property names. Colors only.
      const syntax = /^#(?:[a-f\d]{3}|[a-f\d]{4}|[a-f\d]{6}|[a-f\d]{8})$/i.test(value) || /^(?:rgb|rgba|hsl|hsla|oklch|oklab)\([\deE.%+\-/ ,]+\)$/i.test(value) || /^color\((?:srgb|srgb-linear|display-p3|a98-rgb|prophoto-rgb|rec2020|xyz|xyz-d50|xyz-d65)\s+[\deE.%+\-/ ]+\)$/i.test(value);
      return syntax && (typeof CSS === 'undefined' || !CSS.supports || CSS.supports('color', value));
    }
    return sizeTokens.has(key) && /^\d+(?:\.\d+)?(?:px|rem|em)$/.test(value) && parseFloat(value) <= 128;
  });
}

export function createEmbedThemeBridge(host: Window, parent: Window, origin: string, channel: string) {
  const root = host.document.documentElement;
  const previous = new Map<string, { value: string; priority: string }>();
  const wasDark = root.classList.contains('dark');
  const wasLight = root.classList.contains('light');
  const receive = (event: MessageEvent) => {
    const msg = event.data;
    if (event.origin !== origin || event.source !== parent || !msg || msg.channel !== channel || msg.type !== 'fyso.agents.theme') return;
    const theme: unknown = msg.theme;
    if (!validTheme(theme)) return;
    for (const [key, value] of Object.entries(theme.tokens)) {
      const variable = colorTokens.has(key) || key === 'radius' ? `--${key}` : `--embed-${key}`;
      if (!previous.has(variable)) previous.set(variable, { value: root.style.getPropertyValue(variable), priority: root.style.getPropertyPriority(variable) });
      root.style.setProperty(variable, value);
    }
    root.classList.add('fyso-business-theme');
    root.classList.toggle('dark', theme.scheme === 'dark');
    root.classList.toggle('light', theme.scheme === 'light');
  };
  let disposed = false;
  const suspend = () => {
    host.removeEventListener('message', receive);
    for (const [key, prior] of previous) {
      if (prior.value) root.style.setProperty(key, prior.value, prior.priority); else root.style.removeProperty(key);
    }
    root.classList.remove('fyso-business-theme');
    root.classList.toggle('dark', wasDark);
    root.classList.toggle('light', wasLight);
    previous.clear();
  };
  const resume = () => {
    if (disposed) return;
    host.addEventListener('message', receive);
    parent.postMessage({ type: 'fyso.agents.theme.ready', channel }, origin);
  };
  host.addEventListener('pagehide', suspend);
  host.addEventListener('pageshow', resume);
  resume();
  return () => {
    disposed = true;
    suspend();
    host.removeEventListener('pagehide', suspend);
    host.removeEventListener('pageshow', resume);
  };
}
